import { axiosSecuredInstance } from "configurations/axiosConfig";
import { 
  ActionResult, 
  Agency 
} from "entities/UIModel";
import {
  createHook,
  createStore,
  StoreActionApi,
} from "react-sweet-state";
import { hasValue } from "../shared";
import { 
  ActionResultStatus, 
  DocumentType 
} from "utils/data/enum";
import { ImageArchiveUI } from "entities/UIModel/ImageArchiveUI";
import { TitleSearchGridCriteria } from "entities/UIModel/TitleSearchGridCriteria";
import { Document }  from "entities/UIModel/Document";
import { mapToUiDocuments, mapToUiUWADocuments } from "utils/data/documentHelper";
import { UWADocument } from "entities/ApiModel/UWADocument";

interface State {
  isLoading: boolean;
  error: string | null;
  uploadImageLastSelectedAgency: Agency | null;
  isTitleSearchGridInEditMode: boolean;
  isTitleSearchGridInShowToastMode: boolean;  
  selectedTitleSearchGridItems: ImageArchiveUI[]; 
}

type StoreApi = StoreActionApi<State>;

const setLoading =
  () =>
    ({ setState }: StoreApi) => {
      setState({ isLoading: true });
    };

const setError =
  (error: string | null) =>
    ({ setState }: StoreApi) => {
      setState({ error });
    };

const Store = createStore<State, any>({
  initialState: {
    isLoading: false,
    error: null,
    uploadImageLastSelectedAgency: null,
    isTitleSearchGridInEditMode: false,
    isTitleSearchGridInShowToastMode: false,
    selectedTitleSearchGridItems: [],
  },
  actions: {
    getDocumentsByOrder:
      (searchCriteria: TitleSearchGridCriteria) =>
      async ({ dispatch, setState }: StoreApi) => {
        try {
          setState({ isLoading: true });

          const { data } = await axiosSecuredInstance.get(
            `/Documents/GetDocumentListByOrder?orderId=${searchCriteria?.orderId || 0
            }&showHidden=${searchCriteria?.showHidden || 0
            }&orderBy=${searchCriteria?.sortColumn || ""}`
          );
          const mappedData = dispatch(mapToUiDocuments(data));
          return mappedData; // dispatch(setFilesRecordList(data));
        } catch (error: any) {
          console.log(error);
        }
      },
    GetUwaDocumentListByOrder:
      (searchCriteria: TitleSearchGridCriteria) =>
      async ({ dispatch, setState }: StoreApi) => {
        try {
          setState({ isLoading: true });

          const { data } = await axiosSecuredInstance.get(
            `/Underwriter/GetUwaDocumentListByOrder?orderId=${searchCriteria?.orderId || 0
            }&showHidden=${searchCriteria?.showHidden || 0
            }&orderBy=${searchCriteria?.sortColumn || ""}`
          );
          const mappedData = dispatch(mapToUiUWADocuments(data));
          return mappedData; // dispatch(setFilesRecordList(data));
        } catch (error: any) {
          console.log(error);
        }
      },      
    getDocument:
      (
        documentId: string,
        defaultFilename: string,
        showHidden: number | undefined
      ) =>
      async ({ dispatch, setState }: StoreApi) => {
        let result: ActionResult = {
          error: null,
        };
        try {
          dispatch(setLoading());
          if (!hasValue(showHidden)) {
            showHidden = 0;
          }
          const { data } = await axiosSecuredInstance.get(
            `/Documents/GetDocument?documentId=${documentId}&filename=${defaultFilename}&showHidden=${showHidden}`
          );
          setState({ isLoading: false });
          result.apiResponse = data;
          return result;
          // return data;
        } catch (error) {
          dispatch(setError(String(error)));
          result.error = error;
          return result;
        }
      },
    getDownloadZipFile:
      (documentIds: string, documentTypeCode: string) =>
      async ({ dispatch, setState }: StoreApi): Promise<ActionResult> => {
        const result: ActionResult = {};
        try {
          dispatch(setLoading());
          const { data } = await axiosSecuredInstance.get(
            `/Documents/DownloadMultipleDocuments?documentIds=${documentIds}&documentTypeCode=${documentTypeCode}`,
            {
              responseType: "arraybuffer",
              headers: {
                accept: "application/octet-stream",
              },
            }
          );
          setState({ isLoading: false });
          result.status = ActionResultStatus.Success;
          result.apiResponse = data;
          return result;
        } catch (error) {
          dispatch(setError(String(error)));
          result.status = ActionResultStatus.Failed;
          result.error = error;
          return result;
        }
      },
    getSupplementalDocument:
      (
        documentSupplementalId: string,
        defaultFilename: string,
        showHidden: number | undefined
      ) =>
      async ({ dispatch, setState }: StoreApi): Promise<ActionResult> => {
        let result: ActionResult = {
          error: null,
        };
        try {
          dispatch(setLoading());
          if (!hasValue(showHidden)) {
            showHidden = 0;
          }
          const { data } = await axiosSecuredInstance.get(
            `/Documents/GetSupplementalDocument?documentSupplementalId=${documentSupplementalId}&filename=${defaultFilename}&showHidden=${showHidden}`
          );
          setState({ isLoading: false });
          result.apiResponse = data;
          return result;
        } catch (error) {
          dispatch(setError(String(error)));
          result.error = error;
          return result;
        }
      },
    getDocumentUrl:
      (
        fileId: number,
        productType: string,
        orderId: number,
        documentId: string,
        showHidden: number | undefined
      ) =>
      async ({ dispatch, setState }: StoreApi): Promise<ActionResult> => {
        let result: ActionResult = {
          error: null,
        };

        try {
          dispatch(setLoading());
          if (!hasValue(showHidden)) {
            showHidden = 0;
          }
          const { data, headers } = await axiosSecuredInstance.get(
            `/Documents/GetDocumentInfo?fileID=${fileId}&productType=${productType}&orderID=${orderId}&documentId=${documentId}&showHidden=${showHidden}`,
            { responseType: "json" }
          );
          setState({ isLoading: false });
          result.documentInfo = data.DocumentInfo;
          result.documentId = Number(documentId);
          result.headers = headers;
          return result;
        } catch (error) {
          dispatch(setError(String(error)));
          result.error = error;
          return result;
        }
      },
    getSupplementalDocumentUrl:
      (documentId: number, showHidden: number, integrationKey: string) =>
      async ({ dispatch, setState }: StoreApi): Promise<ActionResult> => {
        let result: ActionResult = {
          error: null,
        };

        try {
          dispatch(setLoading());
          if (!hasValue(showHidden)) {
            showHidden = 0;
          }
          const encoded = btoa(`${documentId}|${integrationKey}`);
          const { data, headers } = await axiosSecuredInstance.get(
            `/Documents/GetSupplementalDocumentInfo?documentSupplementalIdEncoded=${encoded}&showHidden=${showHidden}`,
            { responseType: "json" }
          );
          setState({ isLoading: false });
          result.documentInfo = data.DocumentInfo;
          result.documentId = documentId;
          result.headers = headers;
          return result;
        } catch (error) {
          dispatch(setError(String(error)));
          result.error = error;
          return result;
        }
      },
    getPaymentSheetDocumentUrl:
      (paymentSheetID: number) =>
      async ({ dispatch, setState }: StoreApi): Promise<ActionResult> => {
        let result: ActionResult = {
          error: null,
        };

        try {
          dispatch(setLoading());
          const { data, headers } = await axiosSecuredInstance.get(
            `/Documents/GetPaymentSheetDocumentInfo?paymentSheetID=${paymentSheetID}`,
            { responseType: "json" }
          );
          setState({ isLoading: false });
          result.documentInfo = data.DocumentInfo;
          result.documentName = data.DocumentName;
          result.documentId = paymentSheetID;
          result.headers = headers;
          return result;
        } catch (error) {
          dispatch(setError(String(error)));
          result.error = error;
          return result;
        }
      },
    getTitleSearchOrderSummaryDocumentUrl: (
      orderId: number,
      fileId:number,
      requestId: string
    ) => async ({
        dispatch,
        setState,
      }: StoreApi): Promise<ActionResult> => {
        let result: ActionResult = {
          error: null,
        };

        try {
          dispatch(setLoading());
          const { data, headers } = await axiosSecuredInstance.get(
            `/Order/GetTitleSearchOrderSummary?orderId=${orderId}&fileId=${fileId}&requestId=${requestId}`,
            { responseType: "json" }
          );
          setState({ isLoading: false });
          result.documentInfo = data.DocumentInfo;
          result.documentName = data.DocumentName;
          result.documentId = orderId;
          result.headers = headers;
          return result;
        } catch (error) {
          setState({ isLoading: false });
          dispatch(setError(String(error)));
          result.error = error;
          return result;
        }
      },
    getUwaDocument:
      (
        uwaDocumentId: string,
        defaultFilename: string,
        showHidden: number | undefined
      ) =>
      async ({ dispatch, setState }: StoreApi) => {
        let result: ActionResult = {
          error: null,
        };
        try {
          dispatch(setLoading());
          if (!hasValue(showHidden)) {
            showHidden = 0;
          }
          const { data } = await axiosSecuredInstance.get(
            `/Underwriter/GetUWADocument?uwaDocumentId=${uwaDocumentId}&fileName=${defaultFilename}&showHidden=${showHidden}`
          );
          setState({ isLoading: false });
          result.apiResponse = data;
          return result;
          // return data;
        } catch (error) {
          dispatch(setError(String(error)));
          result.error = error;
          return result;
        }
      },      
    getUwaDocumentUrl:
      (uwaDocumentID: number, showHidden: number) =>
      async ({ dispatch, setState }: StoreApi): Promise<ActionResult> => {
        let result: ActionResult = {
          error: null,
        };

        try {
          dispatch(setLoading());
          if (!hasValue(showHidden)) {
            showHidden = 0;
          }
          const { data, headers } = await axiosSecuredInstance.get(
            `/Underwriter/GetUWADocumentInfo?uwaDocumentId=${uwaDocumentID}&showHidden=${showHidden}`,
            { responseType: "json" }
          );
          setState({ isLoading: false });
          result.documentInfo = data.DocumentInfo;
          result.documentId = uwaDocumentID;
          result.headers = headers;
          return result;
        } catch (error) {
          dispatch(setError(String(error)));
          result.error = error;
          return result;
        }
              },
      getUnderwriterRequestSummaryDocumentUrl: (
          orderId: number,
          fileId: number,
          requestId: string
      ) => async ({
          dispatch,
          setState,
      }: StoreApi): Promise<ActionResult> => {
              let result: ActionResult = {
                  error: null,
              };

              try {
                  dispatch(setLoading());
                  const { data, headers } = await axiosSecuredInstance.get(
                      `/Order/GetUnderwriterRequestSummary?orderId=${orderId}&fileId=${fileId}&requestId=${requestId}`,
                      { responseType: "json" }
                  );
                  setState({ isLoading: false });
                  result.documentInfo = data.DocumentInfo;
                  result.documentName = data.DocumentName;
                  result.documentId = orderId;
                  result.headers = headers;
                  return result;
              } catch (error) {
                  setState({ isLoading: false });
                  dispatch(setError(String(error)));
                  result.error = error;
                  return result;
              }
          },
      getCPLRequestFormUrl:
          () =>
              async ({ dispatch, setState }: StoreApi): Promise<ActionResult> => {
                  let result: ActionResult = {
                      error: null,
                  };

                  try {
                      dispatch(setLoading());
                      const { data, headers } = await axiosSecuredInstance.get(
                          `/documents/GetCPLRequestFormInfo`,
                          { responseType: "json" }
                      );
                      setState({ isLoading: false });
                      result.documentInfo = data.DocumentInfo;
                      result.headers = headers;
                      return result;
                  } catch (error) {
                      dispatch(setError(String(error)));
                      result.error = error;
                      return result;
                  }
              },  
    uploadFiles:
      (
        requestId: string,
        formData: FormData,
        documentTypeCode: string = DocumentType.POLICY
      ) =>
      async ({ dispatch, setState }: StoreApi) => {
        try {
          dispatch(setLoading());
          const { data } = await axiosSecuredInstance.post(
            `/documents/fileupload?requestId=${requestId}&documentTypeCode=${documentTypeCode}`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          setState({ isLoading: false });
          // return data;
          const result: ActionResult = {
            apiResponse: data,
            status: ActionResultStatus.Success,
          };          
          return result;
        } catch (error) {
          dispatch(setError(String(error)));
          const result: ActionResult = {
            error: error,
            status: ActionResultStatus.Failed,
          };
          return result;      
        }
      },
    saveDocument:
      (document: Document) =>
      async ({ dispatch, setState }: StoreApi): Promise<ActionResult> => {
        try {
          const { data, headers } = await axiosSecuredInstance.post(
            `/Documents/SaveDocument`,
            document
          );
          return {
            status: ActionResultStatus.Success,
            headers: headers,
            apiResponse: data,
          };
        } catch (error: any) {
          dispatch(setError(error));
          return {
            status: ActionResultStatus.Failed,
            error: error,
          };
        }
      },
    saveUWADocument:
      (document: UWADocument) =>
      async ({ dispatch, setState }: StoreApi): Promise<ActionResult> => {
        try {
          const { data, headers } = await axiosSecuredInstance.post(
            `/Underwriter/SaveUWADocument`,
            document
          );
          return {
            status: ActionResultStatus.Success,
            headers: headers,
            apiResponse: data,
          };
        } catch (error: any) {
          dispatch(setError(error));
          return {
            status: ActionResultStatus.Failed,
            error: error,
          };
        }
      },      
    deleteDocument:
      (documentId: number) =>
      async ({ dispatch, setState }: StoreApi) => {
        const actionResult: ActionResult = {};
        try {
          const { data, headers } = await axiosSecuredInstance.delete(
            `/Documents/DeleteDocument?documentId=${documentId}`
          );
          actionResult.status = ActionResultStatus.Success;
          actionResult.apiResponse = data;
          actionResult.headers = headers;
          return actionResult;
        } catch (error: any) {
          // console.log(error);
          actionResult.status = ActionResultStatus.Failed;
          actionResult.error = error;
          return actionResult;
        }
      },
    deleteUwaDocument:
      (documentId: number) =>
      async ({ dispatch, setState }: StoreApi) => {
        const actionResult: ActionResult = {};
        try {
          const { data, headers } = await axiosSecuredInstance.delete(
            `/Underwriter/DeleteUWADocument?documentId=${documentId}`
          );
          actionResult.status = ActionResultStatus.Success;
          actionResult.apiResponse = data;
          actionResult.headers = headers;
          return actionResult;
        } catch (error: any) {
          // console.log(error);
          actionResult.status = ActionResultStatus.Failed;
          actionResult.error = error;
          return actionResult;
        }
      },      
    setUploadImageLastSelectedAgency:
      (agency: Agency) =>
      ({ setState }: StoreApi) => {
        setState({
          uploadImageLastSelectedAgency: agency,
        });
      },
    setTitleSearchGridInEditMode:
      (isTitleSearchGridInEditMode: boolean) =>
      ({ setState }: StoreApi) => {
        setState({ isTitleSearchGridInEditMode });
      },
    setTitleSearchGridInShowToastMode:
      (isTitleSearchGridInShowToastMode: boolean) =>
      ({ setState }: StoreApi) => {
        setState({ isTitleSearchGridInShowToastMode });
      },
    updateSelectedTitleSearchGridItem:
      (item: ImageArchiveUI) =>
      ({ setState, getState }: StoreApi) => {
        const currentItems = getState().selectedTitleSearchGridItems || [];
        const updatedItems = currentItems?.filter(
          (d) => d.documentSupplementalID !== item.documentSupplementalID
        );

        if (updatedItems.length === currentItems?.length) {
          updatedItems.push(item);
        }

        setState({
          selectedTitleSearchGridItems: updatedItems,
        });
      },
    updateSelectedTitleSearchGridItems:
      (items: ImageArchiveUI[]) =>
      ({ setState, getState }: StoreApi) => {
        const currentTitleSearchGridItems =
          getState().selectedTitleSearchGridItems || [];
        const newItems = items.filter(
          (item) =>
            !currentTitleSearchGridItems?.find(
              (currentItem) =>
                currentItem.documentSupplementalID ===
                item.documentSupplementalID
            )
        );

        let updatedItems: ImageArchiveUI[] = [];
        if (newItems.length > 0) {
          updatedItems = currentTitleSearchGridItems?.concat(newItems);
        }

        setState({
          selectedTitleSearchGridItems: updatedItems,
        });
      },
    resetSelectedTitleSearchGridItems:
      () =>
      ({ setState }: StoreApi) => {
        setState({
          selectedTitleSearchGridItems: [],
        });
      },
  },
  name: "document",
});

// const getTitleSearchGridStates = (state: State) => ({
//   isTitleSearchGridInEditMode: state.isTitleSearchGridInEditMode,
//   isTitleSearchGridInShowToastMode: state.isTitleSearchGridInShowToastMode,
// })

// const useTitleSearchGridStates = createHook(Store, {
//   selector: getTitleSearchGridStates,
// })

// const getTitleSearchGridItems = (state: State) => ({
//   selectedTitleSearchGridItems: state.selectedTitleSearchGridItems,
// })

// const useTitleSearchGridItems = createHook(Store, {
//   selector: getTitleSearchGridItems,
// })

const hook = createHook(Store);
export const useDocument = () => { return hook(); };

// export {
//   useTitleSearchGridStates,
//   useTitleSearchGridItems
// }
